import { useEffect, useMemo, useState } from "react";
import {
  investors as api,
  type GroupMember,
  type Investor,
} from "../../../api";
import { Link } from "../../../router";
import { ClientTypeIcon } from "../../../components/ClientTypeIcon";

const MAX_MEMBERS = 20;

export function InvestorMembers({
  groupId,
  onCountChange,
}: {
  groupId: number;
  onCountChange?: (n: number) => void;
}) {
  const [members, setMembers] = useState<GroupMember[] | null>(null);
  const [everyone, setEveryone] = useState<Investor[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function apply(list: GroupMember[]) {
    setMembers(list);
    onCountChange?.(list.length);
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listMembers(groupId), api.list()])
      .then(([m, all]) => {
        if (cancelled) return;
        apply(m.members);
        setEveryone(all.investors);
      })
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Eligible: any non-group investor that isn't this group and isn't already a
  // member. Groups can't nest (a group can't be a member of another group).
  const candidates = useMemo(() => {
    const taken = new Set((members ?? []).map((m) => m.member_id));
    return everyone.filter(
      (i) => i.id !== groupId && i.type !== "group" && !taken.has(i.id),
    );
  }, [everyone, members, groupId]);

  // Typeahead: match on name or email, cap the visible list.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? candidates.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            (i.email ?? "").toLowerCase().includes(q),
        )
      : candidates;
    return base.slice(0, 8);
  }, [candidates, query]);

  async function add(memberId: number) {
    setBusy(true);
    setError(null);
    try {
      const { members: list } = await api.addMember(groupId, memberId);
      apply(list);
      setQuery("");
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(memberId: number) {
    setBusy(true);
    setError(null);
    try {
      const { members: list } = await api.removeMember(groupId, memberId);
      apply(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleRep(m: GroupMember) {
    setBusy(true);
    setError(null);
    try {
      const { members: list } = await api.setRepresentative(
        groupId,
        m.member_id,
        !m.is_representative,
      );
      apply(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (members === null) return <div className="muted">Loading…</div>;

  const full = members.length >= MAX_MEMBERS;

  return (
    <>
      <div className="section-head">
        <div>
          <h2 className="section-title">Members</h2>
          <p className="muted">
            The persons this group consists of (couples / joint investors).
          </p>
        </div>
        <span className="muted count-badge">
          {members.length}/{MAX_MEMBERS}
        </span>
      </div>

      {error && <div className="banner-error">{error}</div>}

      {members.length === 0 ? (
        <div className="empty">
          <p>No members yet.</p>
        </div>
      ) : (
        <ul className="member-list">
          {members.map((m) => (
            <li key={m.member_id} className="member-card">
              <ClientTypeIcon type={m.type} className="member-icon" />
              <div className="member-main">
                <div className="member-name">
                  <Link to={`/app/crm/${m.member_id}`}>{m.name}</Link>
                  {m.is_representative && (
                    <span className="pill kyc-approved">Representative</span>
                  )}
                </div>
                <p className="muted member-contact">
                  {[m.email, m.phone].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <div className="member-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleRep(m)}
                  disabled={busy}
                >
                  {m.is_representative
                    ? "Unset representative"
                    : "Make representative"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => remove(m.member_id)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {full ? (
        <p className="muted">Maximum of {MAX_MEMBERS} members reached.</p>
      ) : candidates.length === 0 ? (
        <p className="muted">No other investors available to add as members.</p>
      ) : (
        <div className="member-combo">
          <input
            type="search"
            className="search"
            placeholder="Add a member — search investors by name or email…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            disabled={busy}
          />
          {open && (
            <ul className="combo-results">
              {matches.length === 0 ? (
                <li className="combo-empty muted">No matching investors</li>
              ) : (
                matches.map((i) => (
                  <li key={i.id}>
                    <button
                      type="button"
                      className="combo-option"
                      // fire before the input's blur so the pick lands
                      onMouseDown={(e) => {
                        e.preventDefault();
                        add(i.id);
                      }}
                      disabled={busy}
                    >
                      <ClientTypeIcon type={i.type} className="member-icon" />
                      <span className="combo-name">{i.name}</span>
                      {i.email && (
                        <span className="muted combo-email">{i.email}</span>
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
